const isNumberStringOnly = (value: string): boolean =>
  /^[-+]?[0-9]*\.?[0-9]+([eE][-+]?[0-9]+)?$/.test(value);

/**
 *
 * @param {any} value
 * @returns {boolean}
 */
export const isNumberString = (value: any): value is string => {
  if (value == null || value === '') return false;
  if (typeof value === 'string') return isNumberStringOnly(value);
  return false;
};

export const isNumeric = (value: any): boolean => {
  if (value == null || value === '') return false;
  if (typeof value === 'number') return true;
  if (typeof value === 'string') return isNumberStringOnly(value);
  return false;
};

export const getNumber = (number: any): number => {
  if (!isNumeric(number)) return 0;
  if (typeof number === 'number') return number;

  const num = parseInt(number);
  return isNaN(num) ? 0 : num;
};

export const getFloatNumber = (number: any): number => {
  if (!isNumeric(number)) return 0;
  if (typeof number === 'number') return number;

  const num = parseFloat(number);
  return isNaN(num) ? 0 : num;
};

export const formatNumber = (value: any, decimal: number = 2): string => {
  if (!isNumeric(value)) return value;
  return value.toFixed(decimal);
};

/**
 * Converts a positive integer to a Roman numeral (using an extended style for numbers greater than 3999).
 *
 * @param {number} num
 * @returns {string}
 *
 * @warning Numbers greater than 3999 are not standardized in the classical Roman numeral system.
 *          Here, the function will return an "extended" form (e.g., 4000 -> "MMMM").
 *          Historically, Romans used an overline to denote multiples of 1000, for example: \(\overline{IV}\) for 4000.
 */
export const toRoman = (num: number): string => {
  if (!isNumeric(num) || num <= 0) {
    return '';
  }

  if (num > 3999) {
    console.warn(
      'Numbers greater than 3999 are not standardized in the classical Roman numeral system.'
    );
  }

  const romanNumerals = [
    { value: 1000, symbol: 'M' },
    { value: 900, symbol: 'CM' },
    { value: 500, symbol: 'D' },
    { value: 400, symbol: 'CD' },
    { value: 100, symbol: 'C' },
    { value: 90, symbol: 'XC' },
    { value: 50, symbol: 'L' },
    { value: 40, symbol: 'XL' },
    { value: 10, symbol: 'X' },
    { value: 9, symbol: 'IX' },
    { value: 5, symbol: 'V' },
    { value: 4, symbol: 'IV' },
    { value: 1, symbol: 'I' }
  ];

  let result = '';

  for (const { value, symbol } of romanNumerals) {
    // While the current value fits into `num`, append symbol and reduce `num`.
    while (num >= value) {
      result += symbol;
      num -= value;
    }
  }

  return result;
};
